import React, { useState, useEffect, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import { Col, Form, Row } from "react-bootstrap";
import Button from "../CustomButton/CustomButton";
import { useSelector } from "react-redux";
import BeatLoader from "react-spinners/BeatLoader";
import toastr from "toastr";
import "./UserInfo.scss";
import { USER_INFO_TYPES } from "../../constants/user-info";
import { ROlES } from "../../constants/settings";
import CustomInput from "../../common/CustomInput/CustomInput";
import { EMAIL_PATTERN } from "constants/patterns";
import { editUser, inviteUser } from "../../services/users/users.service";
import { createUser } from "../../services/auth/signup";
import { useDispatch } from "react-redux";
import { setShouldUpdateUsers } from "store/actions/users.actions";
import { isCurrentUser } from "helpers/user.helper";
import { USER_INFO_TEXT_VARIABLES } from "../../constants/user-info";
import { setShouldUpdateInvites } from "store/actions/users.actions";
import AccessAssignmentPanel from "../AccessAssignmentPanel/AccessAssignmentPanel";
import {
  emptyAccess,
  getRoleDefaultAccess,
  resolveAccessibleBusinessIds,
  roleSeesAllLocations
} from "../../services/access/accessResolver";

const UserInfo = ({ type, goBack, closeModal, hideRoleDropdown = false, user, onGetUsers }) => {
  const businessSelector = useSelector(state => state.business);
  const { businesses, business } = businessSelector;
  const regions = useSelector(state => state.regions.regions);
  const [error, setError] = useState("");
  const dispatch = useDispatch();

  const [formValues, setFormValues] = useState({
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    businessId: user?.businessId || business.businessId || "",
    role: user?.role || "",
    access: user?.access || emptyAccess()
  });

  // Tracks whether the admin has manually interacted with the Access panel.
  // Until they do, role/businessId changes re-seed `access` with the role
  // default (mirrors what the backend would seed). Once touched, manual edits
  // are preserved even if the admin later flips the role, so an admin's
  // explicit grants are not silently overwritten. When editing an existing
  // user, their persisted `access` is already correct on mount, so treat it
  // as "touched" from the start — otherwise the seeding effect below fires
  // immediately and clobbers the saved access with the role default.
  const accessTouched = useRef(type === USER_INFO_TYPES.UPDATE_USER);

  const form = useForm();
  const { handleSubmit, triggerValidation, formState } = form;
  const [loading, setLoading] = useState(false);
  const [roleValue, setRoleValue] = useState(user?.role || ROlES[0].role);
  const [role, setRole] = useState(null);

  // Region + business normalization for the AccessAssignmentPanel. The panel
  // expects plain objects with `businessId`/`regionId`/`id`/`isArea`/
  // `parentRegion`/`name`. businesses already carry these in Redux; regions
  // docs arrive with `id` set by the Admin layout's region subscription.
  const regionsForPanel = useMemo(() => {
    return (regions || []).map(r => ({
      id: r.id,
      name: r.name,
      isArea: r.isArea,
      parentRegion: r.parentRegion
    }));
  }, [regions]);
  const businessesForPanel = useMemo(() => {
    return (businesses || []).map(b => ({
      businessId: b.businessId,
      name: b.name,
      regionId: b.regionId
    }));
  }, [businesses]);
  const lockedBusinessIds = useMemo(
    () => (formValues.businessId ? [formValues.businessId] : []),
    [formValues.businessId]
  );

  const onSubmit = async values => {
    setLoading(true);
    setError("");
    const data = {
      ...values,
      email: values?.email?.toLowerCase().trim()
    };
    const fullName = data.fullName.split(" ");
    data.firstName = fullName.shift();
    data.lastName = fullName.join(" ");
    delete data.fullName;

    const accountId = businesses.find(b => b.businessId === values.businessId).accountId;

    // Guarantee the user's primary location is always part of their access.
    // The panel visually locks + force-checks the primary sub-child, but if no
    // checkbox was toggled this run (or the primary was just changed via the
    // dropdown, which doesn't go through the panel at all), `formValues.access`
    // may not actually cover the primary id. Resolve for real rather than
    // guessing from array lengths, so an unrelated region/area grant can't be
    // mistaken for coverage.
    const primaryBusinessId = formValues.businessId;
    let access = formValues.access || emptyAccess();
    if (primaryBusinessId && !access.allLocations) {
      const resolved = resolveAccessibleBusinessIds(access, businessesForPanel, regionsForPanel);
      if (!resolved.has(primaryBusinessId)) {
        access = { ...access, businessIds: [...access.businessIds, primaryBusinessId] };
      }
    }

    switch (type) {
      case USER_INFO_TYPES.CREATE_USER:
        try {
          await createUser({ accountId, ...data, access });
          await dispatch(setShouldUpdateUsers(true));
          toastr.success("User created.");
        } catch (error) {
          setError(error.message);
          setLoading(false);
          return;
        }
        break;
      case USER_INFO_TYPES.INVITE_USER:
        try {
          await inviteUser({ ...data, access });
          await dispatch(setShouldUpdateInvites(true));
          toastr.success("User invited.");
        } catch (error) {
          setError(error.message);
          setLoading(false);
          return;
        }
        break;
      case USER_INFO_TYPES.UPDATE_USER:
        try {
          const editData = {
            firstName: data.firstName,
            lastName: data.lastName,
            businessId: data.businessId,
            access
          };
          // check if it's current user, than formValues.role is "", and no need to edit role to ""
          if (formValues.role) {
            editData.role = formValues.role;
          }
          await editUser(user.userId, { user: editData, accountId, isCurrentUser: isCurrentUser(user) });
          await onGetUsers(accountId);
          toastr.success("User updated!");
        } catch (error) {
          console.error("User didn't update, something went wront.");
        }
        break;
      default:
        toastr.error("Something went wrong, try again later.");
        break;
    }
    closeModal();
    setLoading(false);
  };

  useEffect(() => {
    setRole(ROlES.find(role => role.role === roleValue));
  }, [roleValue]);

  // Role-default seeding: whenever role or primary businessId changes and the
  // admin has NOT manually touched the Access panel, repopulate `access` with
  // whatever the backend would have seeded for that role at invite time. This
  // keeps the panel in sync for the common "pick role then save" flow while
  // never clobbering an admin's explicit grants once they've made any change.
  //
  // Roles that always see every location (admin, etc.) are the one exception:
  // their access is forced to allLocations unconditionally, even if the
  // panel was previously touched or this is an existing user being edited —
  // the backend enforces the same invariant, so the panel would just be
  // overridden on save anyway. Once the role is changed away from one of
  // these roles, normal editing (and touched-tracking) resumes.
  useEffect(() => {
    if (roleSeesAllLocations(formValues.role)) {
      setFormValues(prev =>
        prev.access?.allLocations &&
        !prev.access.regionIds.length &&
        !prev.access.areaIds.length &&
        !prev.access.businessIds.length
          ? prev
          : { ...prev, access: { allLocations: true, regionIds: [], areaIds: [], businessIds: [] } }
      );
      return;
    }
    if (accessTouched.current) return;
    if (!formValues.role || !formValues.businessId || !businessesForPanel.length) return;
    const seeded = getRoleDefaultAccess(formValues.role, formValues.businessId, businessesForPanel, regionsForPanel);
    setFormValues(prev => ({ ...prev, access: seeded }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValues.role, formValues.businessId, businessesForPanel, regionsForPanel]);

  const onAccessChange = nextAccess => {
    accessTouched.current = true;
    setFormValues(prev => ({ ...prev, access: nextAccess }));
  };

  const onCancel = () => {
    if (type === USER_INFO_TYPES.UPDATE_USER) return closeModal();
    return goBack();
  };

  const handleChangeField = event => {
    const { name, value } = event.target;
    setFormValues({ ...formValues, [name]: value });
  };

  const handleChangeRole = event => {
    handleChangeField(event);
    setRoleValue(event.target.value);
  };

  return (
    <Form noValidate className="add-reservation" onSubmit={handleSubmit(onSubmit)}>
      <Row>
        <Col className="add-reservation__title" md={12}>
          {USER_INFO_TEXT_VARIABLES.HEADER[type]}
        </Col>
      </Row>
      <Row className="add-user-info__container">
        <Col md={8} className="add-user-info__col add-user-info__col--form">
          <CustomInput
            form={form}
            name="fullName"
            label="Full name"
            disabled={loading}
            required
            maxLength={80}
            placeholder="Full name"
            defaultValue={(formValues.firstName + " " + formValues.lastName).trim()}
          />
          {type !== USER_INFO_TYPES.UPDATE_USER && (
            <CustomInput
              form={form}
              name="email"
              label="Email"
              placeholder="Email"
              disabled={loading}
              type="email"
              required
              pattern={{
                value: EMAIL_PATTERN,
                message: "Please enter a valid email"
              }}
              onChange={() => {
                formState.touched.repeatEmail
                  ? triggerValidation(["email", "repeatEmail"])
                  : triggerValidation("email");
              }}
            />
          )}
          <CustomInput
            form={form}
            name="businessId"
            label="Primary location"
            disabled={loading}
            type="select"
            required
            options={businesses.map(b => ({ label: b.name, value: b.businessId }))}
            value={formValues.businessId}
            onChange={handleChangeField}
          />
          {!hideRoleDropdown && (
            <CustomInput
              form={form}
              name="role"
              label="Role"
              disabled={loading}
              type="select"
              required
              onChange={handleChangeRole}
              options={ROlES.map(r => ({ label: r.name, value: r.role }))}
              value={formValues.role}
            />
          )}
          {type === USER_INFO_TYPES.CREATE_USER && (
            <CustomInput
              form={form}
              name="password"
              label="Password"
              disabled={loading}
              type={"password"}
              required
              minLength={8}
              placeholder="Password"
            />
          )}
          <div className="access-assignment-section add-user-info__access">
            <p className="role-permissions-title">LOCATION ACCESS</p>
            <AccessAssignmentPanel
              access={formValues.access}
              onChange={onAccessChange}
              regions={regionsForPanel}
              businesses={businessesForPanel}
              disabled={loading || roleSeesAllLocations(formValues.role)}
              lockedBusinessIds={lockedBusinessIds}
            />
          </div>
        </Col>
        <Col md={4} className="add-user-info__col add-user-info__col--permissions">
          {role && (
            <div className="role-permisions">
              <p className="role-permissions-title"> {role.name.toUpperCase() + " PERMISSIONS:"}</p>
              <ul className="role-permissions">
                {role.permissions.map((bp, idx) => {
                  return <li key={idx}>{bp}</li>;
                })}
              </ul>
            </div>
          )}
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <div className="add-reservation__actions">
            {error && <p className="add-guest__error">{error}</p>}
            <Button onClick={onCancel} className="add-reservation__cancel" bsStyle="info" pullRight type="button">
              {USER_INFO_TEXT_VARIABLES.CANCEL_BTN_TEXT[type]}
            </Button>
            <Button bsStyle="info" pullRight fill type="submit">
              {loading ? (
                <BeatLoader margin={2.5} size={10} color={"#fff"} />
              ) : (
                <span>{USER_INFO_TEXT_VARIABLES.SUCCESS_BTN_TEXT[type]}</span>
              )}
            </Button>
          </div>
        </Col>
      </Row>
    </Form>
  );
};

export default UserInfo;
